import React from 'react';
import {
	ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
	ChevronsUp, ChevronsDown, ChevronsLeft, ChevronsRight,
	Link2, MessageSquare,
} from 'react-feather';
import Tooltip from '../../components/popups/Tooltip';

// Centre rail between the two compare panes (RES-561): a white lozenge (per the
// Idea-2 design) holding first/prev/count/next/last PAGE navigation in primary
// blue (the DraftNotePreviewModal page-guide pattern — no wrap-around), plus the
// comments toggle and sync-scroll lock (kept from the original implementation,
// restyled). `horizontal` renders the same controls as a row — the comments panel
// embeds it at its top so page navigation stays reachable while comments are open
// (the panel has its own close button, so pass no onToggleComments there).
// Tooltips are the styled Tooltip component (follow-ups item 2).
const CompareCentreRail = ({
	pageCount, pageIndex, onFirstPage, onPrevPage, onNextPage, onLastPage,
	syncScroll, onToggleSync, horizontal, onToggleComments,
}) => {
	const atFirst = pageIndex <= 0;
	const atLast = pageIndex >= pageCount - 1;
	const FirstIcon = horizontal ? ChevronsLeft : ChevronsUp;
	const PrevIcon = horizontal ? ChevronLeft : ChevronUp;
	const NextIcon = horizontal ? ChevronRight : ChevronDown;
	const LastIcon = horizontal ? ChevronsRight : ChevronsDown;
	const placement = horizontal ? 'top' : 'right';
	// One shape for every rail control: the label and disabled flag each feed
	// BOTH the Tooltip and the button, so they can't drift apart. The Tooltip's
	// key flips with `disabled` so a tooltip that is showing at the moment its
	// button disables remounts away — the shared Popup's mouse-leave handler
	// early-returns when disabled, so a lingering popup could otherwise never
	// be dismissed.
	const railButton = ({ label, icon, onClick, disabled = false, muted = false }) => (
		<Tooltip
			key={label + (disabled ? '|off' : '|on')}
			label={label}
			placement={placement}
			delay={1000}
			disabled={disabled}
		>
			<button
				type="button"
				className={'compare-rail-button' + (muted ? ' compare-rail-button-muted' : '')}
				aria-label={label}
				disabled={disabled}
				onClick={onClick}
			>
				{icon}
			</button>
		</Tooltip>
	);
	return (
		<div className={'compare-centre-rail' + (horizontal ? ' compare-centre-rail-horizontal' : '')}>
			<div className="compare-rail-pill">
				{onToggleComments && (
					<>
						{railButton({ label: 'Show comments', icon: <MessageSquare size={18} />, onClick: onToggleComments })}
						<div className="compare-rail-divider" />
					</>
				)}
				{railButton({
					label: syncScroll ? 'Scrolling together — click to unlink' : 'Scrolling independently — click to link',
					icon: <Link2 size={18} />,
					onClick: onToggleSync,
					// Off-state for the sync-scroll toggle: control stays, icon goes muted.
					muted: !syncScroll,
				})}
				<div className="compare-rail-divider" />
				{railButton({ label: 'First page', icon: <FirstIcon size={20} />, onClick: onFirstPage, disabled: atFirst })}
				{railButton({ label: 'Previous page', icon: <PrevIcon size={20} />, onClick: onPrevPage, disabled: atFirst })}
				<div className="compare-rail-count">
					{`${pageIndex + 1} / ${pageCount}`}
				</div>
				{railButton({ label: 'Next page', icon: <NextIcon size={20} />, onClick: onNextPage, disabled: atLast })}
				{railButton({ label: 'Last page', icon: <LastIcon size={20} />, onClick: onLastPage, disabled: atLast })}
			</div>
		</div>
	);
};

export default CompareCentreRail;
